import React, { useEffect, useRef, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import CustomAutocomplete from "./common/ui/CustomAutocomplete";
import DraggableResizablePopup from "./common/ui/DraggableResizablePopup";
import { RestfulUrls } from "../constants/restfulUrls";

interface FloatingEditSummaryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  text?: string;
  summaryTitle?: string;
  handleUpdateSummary: (data: string, title: string, interventionLabelId?: number) => void;
  countryOptions: Array<any>;
}

const FloatingEditSummaryPopup: React.FC<FloatingEditSummaryPopupProps> = ({
  isOpen,
  onClose,
  text = "",
  summaryTitle = "",
  handleUpdateSummary,
  countryOptions,
}) => {
  const [summary, setSummary] = useState<string>("");
  const [currentLabelValue, setCurrentLabelValue] = useState<{ label: string; value: string }>({ label: "", value: "" });
  const [selectedLabelId, setSelectedLabelId] = useState<number | undefined>(undefined);

  const addLabelURL = `${RestfulUrls.BASE_URL}${RestfulUrls.ADD_INTERVENTION_LABEL}`;
  const onCancelRef = useRef<() => void>();
  const prevIsOpenRef = useRef<boolean>(false);
  const userHasChangedValueRef = useRef<boolean>(false);

  useEffect(() => {
    // Only initialize when popup transitions from closed to open
    const isOpening = isOpen && !prevIsOpenRef.current;
    
    if (isOpening) {
      // Reset user change flag when popup opens
      userHasChangedValueRef.current = false;
      
      // Set summary immediately when popup opens
      setSummary(text || "");
      
      // Initialize label value from props when popup opens
      const titleToSet = summaryTitle || "";
      
      if (titleToSet && countryOptions.length > 0) {
        const selectedOption = countryOptions.find(opt => opt.label === titleToSet);
        if (selectedOption) {
          const labelValue = { label: selectedOption.label, value: String(selectedOption.value) };
          setSelectedLabelId(Number(selectedOption.value));
          setCurrentLabelValue(labelValue);
        } else if (titleToSet) {
          // If title exists but not in options, use it as fallback
          setCurrentLabelValue({ label: titleToSet, value: titleToSet });
        } else {
          setCurrentLabelValue({ label: "", value: "" });
        }
      } else if (titleToSet) {
        // If we have a title but options aren't loaded yet, set it anyway
        setCurrentLabelValue({ label: titleToSet, value: titleToSet });
      } else {
        setCurrentLabelValue({ label: "", value: "" });
      }
    } else if (isOpen && !userHasChangedValueRef.current && summaryTitle && countryOptions.length > 0) {
      // If popup is already open, options just loaded, and user hasn't made changes yet
      // This handles the case where options load asynchronously after popup opens
      // Only update if user hasn't changed the value
      const selectedOption = countryOptions.find(opt => opt.label === summaryTitle);
      if (selectedOption && currentLabelValue.label !== selectedOption.label) {
        const labelValue = { label: selectedOption.label, value: String(selectedOption.value) };
        setSelectedLabelId(Number(selectedOption.value));
        setCurrentLabelValue(labelValue);
      }
    }
    
    // Reset user change flag when popup closes
    if (!isOpen && prevIsOpenRef.current) {
      userHasChangedValueRef.current = false;
    }
    
    // Update the ref to track current state
    prevIsOpenRef.current = isOpen;
  }, [isOpen, text, summaryTitle, countryOptions]);

  const handleSummaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSummary(event.target.value);
  };

  const handleCancel = () => {
    if (onCancelRef.current) {
      onCancelRef.current();
    }
    onClose();
  };

  const handleUpdate = () => {
    // Use the current label value from state
    const currentTitle = currentLabelValue.label || "";
    const currentSummary = summary || text || "";
    
    // Get label ID from currentLabelValue if it has a numeric value, otherwise try to find it
    let labelId: number | undefined = undefined;
    if (currentLabelValue.value && currentLabelValue.value !== currentLabelValue.label) {
      // If value is different from label, it's likely a numeric ID
      const numericValue = Number(currentLabelValue.value);
      if (!isNaN(numericValue)) {
        labelId = numericValue;
      }
    }
    
    // Fallback: try to find in options or use selectedLabelId
    if (!labelId && currentTitle) {
      const selectedOption = countryOptions.find(opt => opt.label === currentTitle);
      if (selectedOption) {
        labelId = Number(selectedOption.value);
      } else if (selectedLabelId) {
        labelId = selectedLabelId;
      }
    }
    
    handleUpdateSummary(currentSummary, currentTitle, labelId);
  };

  return (
    <DraggableResizablePopup
      isOpen={isOpen}
      onClose={onClose}
      //title="Edit Intervention Summary"
      defaultWidth={550}
      defaultHeight={550}
      storageKey="intervention-summary-edit-popup"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
        <Box sx={{ position: "relative" }}>
          <CustomAutocomplete
            key={`${isOpen}-${summaryTitle}`}
            autocompleteClass="popup-autocomplete"
            options={countryOptions}
            addUrl={addLabelURL}
            value={currentLabelValue}
            onChange={(newValue) => {
              // Mark that user has changed the value - this prevents useEffect from resetting it
              userHasChangedValueRef.current = true;
              
              // Update state immediately when user changes the selection
              const valueToSet = newValue || { label: "", value: "" };
              setCurrentLabelValue(valueToSet);
              
              if (newValue && newValue.value) {
                setSelectedLabelId(Number(newValue.value));
              } else {
                // Clear state if value is cleared
                setSelectedLabelId(undefined);
              }
            }}
            label="Intervention Label"
            onOpen={() => {
              // Don't clear or modify the value on open - keep the current selection
              // This allows the user to change the selection freely
            }}
            requiredMessage="Label is required."
            registerCancel={(cancelFn) => {
              onCancelRef.current = cancelFn;
            }}
          />
        </Box>

        <TextField
          id="outlined-multiline-flexible"
          multiline
          rows={12}
          value={summary}
          onChange={handleSummaryChange}
          fullWidth
          placeholder="Enter intervention summary text..."
          sx={{ flex: 1 }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: "auto"}}>
          <Button
            onMouseDown={handleCancel}
            variant="outlined"
            sx={{ textTransform: "none", minWidth: "100px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "none", minWidth: "100px" }}
            disabled={!summary.trim() || !currentLabelValue.label.trim()}
            onClick={handleUpdate}
          >
            Update Summary
          </Button>
        </Box>
      </Box>
    </DraggableResizablePopup>
  );
};

export default FloatingEditSummaryPopup;

